/** Members tab — list, invite, update role, remove members */

import { useState, type FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Trash2, UserPlus } from 'lucide-react'
import { ROLES, type Role } from '@agentwiki/shared'
import { apiClient } from '../../lib/api-client'
import { cn } from '../../lib/utils'
import { useT, type TranslationKey } from '../../i18n'
import { SettingsSectionHeader } from './settings-section-header'
import { buttonPrimaryClass, buttonSecondaryClass, cardClass, errorTextClass, iconButtonClass, inputClass, labelClass } from '../ui/styles'

interface Member {
  id: string
  userId: string
  role: string
  joinedAt: string
  userName: string
  userEmail: string
  userAvatar: string | null
}

export const ROLE_LABEL_KEYS: Record<Role, TranslationKey> = {
  admin: 'settings.members.role.admin',
  editor: 'settings.members.role.editor',
  viewer: 'settings.members.role.viewer',
  agent: 'settings.members.role.agent',
}

const errorMessage = (err: unknown) => (err instanceof Error ? err.message : String(err))

export function MembersTab() {
  const t = useT()
  const qc = useQueryClient()
  const [showInvite, setShowInvite] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<Role>('viewer')

  const { data, isLoading, isError } = useQuery({
    queryKey: ['members'],
    queryFn: () => apiClient.get<{ members: Member[] }>('/api/members'),
  })

  const invite = useMutation({
    mutationFn: (body: { email: string; role: string }) => apiClient.post<{ member: Member }>('/api/members/invite', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['members'] })
      setInviteEmail('')
      setInviteRole('viewer')
      setShowInvite(false)
      toast.success(t('settings.members.invited'))
    },
  })

  const updateRole = useMutation({
    mutationFn: ({ id, role }: { id: string; role: string }) => apiClient.patch(`/api/members/${id}`, { role }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members'] }),
    onError: (err) => toast.error(t('settings.saveFailed', { message: errorMessage(err) })),
  })

  const remove = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api/members/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['members'] }),
    onError: (err) => toast.error(t('settings.saveFailed', { message: errorMessage(err) })),
  })

  const handleInvite = (e: FormEvent) => {
    e.preventDefault()
    if (inviteEmail.trim()) invite.mutate({ email: inviteEmail.trim(), role: inviteRole })
  }

  const members = data?.members ?? []

  return (
    <div className="space-y-4">
      <SettingsSectionHeader
        title={t('settings.members.title')}
        description={t('settings.members.subtitle')}
        action={
          <button
            type="button"
            onClick={() => {
              setShowInvite((v) => !v)
              invite.reset()
            }}
            className={buttonPrimaryClass}
          >
            <UserPlus className="h-4 w-4" aria-hidden="true" />
            {t('settings.members.invite')}
          </button>
        }
      />

      {showInvite && (
        <form onSubmit={handleInvite} className={cn(cardClass, 'space-y-3 p-4')}>
          <p className="text-sm text-fg-muted">{t('settings.members.inviteHint')}</p>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1 space-y-1.5">
              <label htmlFor="invite-email" className={labelClass}>
                {t('settings.members.email')}
              </label>
              <input
                id="invite-email"
                type="email"
                placeholder="user@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                className={inputClass}
              />
            </div>
            <div className="space-y-1.5 sm:w-40">
              <label htmlFor="invite-role" className={labelClass}>
                {t('settings.members.roleLabel')}
              </label>
              <select id="invite-role" value={inviteRole} onChange={(e) => setInviteRole(e.target.value as Role)} className={inputClass}>
                {ROLES.map((r) => (
                  <option key={r} value={r}>
                    {t(ROLE_LABEL_KEYS[r])}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {invite.isError && <p role="alert" className={errorTextClass}>{errorMessage(invite.error)}</p>}
          <div className="flex flex-wrap gap-2">
            <button type="submit" disabled={invite.isPending || !inviteEmail.trim()} className={buttonPrimaryClass}>
              {invite.isPending ? t('settings.members.inviting') : t('settings.members.sendInvite')}
            </button>
            <button type="button" onClick={() => setShowInvite(false)} className={buttonSecondaryClass}>
              {t('common.cancel')}
            </button>
          </div>
        </form>
      )}

      {isLoading ? (
        <p className="py-6 text-center text-sm text-fg-subtle">{t('common.loading')}</p>
      ) : isError ? (
        <p role="alert" className={errorTextClass}>{t('settings.loadError')}</p>
      ) : members.length === 0 ? (
        <p className="py-6 text-center text-sm text-fg-subtle">{t('settings.members.empty')}</p>
      ) : (
        <ul className={cn(cardClass, 'divide-y divide-border')}>
          {members.map((m) => (
            <MemberRow
              key={m.id}
              member={m}
              onRoleChange={(role) => {
                if (window.confirm(t('settings.members.roleConfirm', { name: m.userName, role: t(ROLE_LABEL_KEYS[role]) }))) {
                  updateRole.mutate({ id: m.id, role })
                }
              }}
              onRemove={() => {
                if (window.confirm(t('settings.members.removeConfirm', { name: m.userName }))) remove.mutate(m.id)
              }}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function MemberRow({ member, onRoleChange, onRemove }: { member: Member; onRoleChange: (role: Role) => void; onRemove: () => void }) {
  const t = useT()
  const initials = member.userName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  return (
    <li className="flex flex-wrap items-center gap-3 p-3 sm:flex-nowrap">
      {member.userAvatar ? (
        <img src={member.userAvatar} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />
      ) : (
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-muted text-xs font-semibold text-fg-muted" aria-hidden="true">
          {initials}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-fg">{member.userName}</p>
        <p className="truncate text-xs text-fg-subtle">{member.userEmail}</p>
      </div>
      <select
        value={member.role}
        onChange={(e) => onRoleChange(e.target.value as Role)}
        aria-label={t('settings.members.roleFor', { name: member.userName })}
        className={cn(inputClass, 'w-auto py-1.5')}
      >
        {ROLES.map((r) => (
          <option key={r} value={r}>
            {t(ROLE_LABEL_KEYS[r])}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={onRemove}
        aria-label={t('settings.members.removeNamed', { name: member.userName })}
        title={t('settings.members.removeNamed', { name: member.userName })}
        className={cn(iconButtonClass, 'text-danger hover:bg-danger-soft hover:text-danger')}
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </li>
  )
}
